import { isNil } from 'lodash';

import useDataFetcher from '~/components/useDataFetcher';
import type { FundAccountRulesSourceAvailability } from '~/server_shared/openapi/data-contracts';

/**
 * Whether the fund-account engine can actually serve this payment method — the readiness the "Use
 * rules" option is gated on.
 *
 * The server answers it: readiness is a configured default behaviour AND an enabled rule whose scope
 * covers this method *and this configuration's sub-organization*, and that scope check excludes the
 * portal-routed methods no rule can target. Deriving any of that from the rules list in the browser
 * would be a second implementation of it.
 *
 * `subOrganizationId` is the row the dialog was opened on, and is omitted for the org-level row — which
 * is bound to no one sub-organization, so the server narrows to none for it.
 *
 * Unavailable is the fail-safe answer — for an error and for a fetch that has not settled. The write
 * path does not stand behind the affordance: `payments_config_manager._validate_payment_method_config`
 * parses the block but deliberately leaves its prerequisites unvalidated ("whether the engine can serve
 * a rules-sourced method is decided at approval, not at configuration time"), so a configuration saved
 * against an engine that cannot serve it is accepted here and blocks — or falls to the org default — on
 * every payment. Offering the option ahead of the answer is therefore not a save that bounces back; it
 * is a routing promise nothing later checks.
 *
 * `shouldOpenErrorDialog: false` rather than `shouldReportAxiosError: false`: a failed probe is still
 * reported to `/api/v1/errors`, but it is not something the admin who opened this dialog to edit
 * something else has to dismiss — the option staying unavailable already says what the failure means.
 *
 * The identity check on the response is kept on top of `!isLoading`: the schema echoes back what it
 * answered for precisely so a late response cannot be applied to whichever configuration is on screen
 * when it lands, and `isLoading` only turns back on once the params-sync effect has run.
 */
export const useFundAccountRulesAvailability = (
  organizationId: number,
  paymentMethod: string,
  subOrganizationId?: number | null
): { isRulesSourceAvailable: boolean } => {
  const { data, isLoading, isError } = useDataFetcher<FundAccountRulesSourceAvailability>(
    `/sysconfig/api/v1/organizations/${organizationId}/fund_accounts/rules_source_availability/${paymentMethod}`,
    // Omitted rather than sent as null for the org-level row: the route's query model reads an absent
    // param as "not asking about one sub-organization", which is that row's answer.
    { params: isNil(subOrganizationId) ? undefined : { sub_organization_id: subOrganizationId } },
    { shouldOpenErrorDialog: false }
  );

  return {
    isRulesSourceAvailable:
      !isLoading &&
      !isError &&
      !!data &&
      data.payment_method === paymentMethod &&
      data.sub_organization_id === (subOrganizationId ?? null) &&
      data.is_rules_source_available,
  };
};
